import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Clock, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MobileLayout } from "@/components/layouts/MobileLayout";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import API from "@/utils/api";

const STATUS_LABELS = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  preparing: "Preparando",
  ready: "Listo",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          withCredentials: true,
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchOrders();
    }
  }, [user]);

  if (!user) {
    return (
      <MobileLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
          <Package className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="font-heading text-xl font-bold mb-2">
            Inicia sesión para ver tus pedidos
          </h2>
          <p className="text-muted-foreground text-center mb-6">
            Accede a tu cuenta para ver el historial de pedidos
          </p>
          <Button
            className="rounded-full"
            onClick={() => navigate("/login")}
            data-testid="orders-login-btn"
          >
            Iniciar sesión
          </Button>
        </div>
      </MobileLayout>
    );
  }

  if (isLoading) {
    return (
      <MobileLayout>
        <div className="p-4 space-y-4">
          <div className="h-8 w-40 bg-gray-100 rounded animate-pulse" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="px-4 py-4">
        <h1 className="font-heading text-2xl font-bold mb-6">Mis pedidos</h1>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="font-heading text-lg font-bold mb-2">
              No tienes pedidos aún
            </h2>
            <p className="text-muted-foreground mb-6">
              ¡Haz tu primer pedido ahora!
            </p>
            <Button
              className="rounded-full"
              onClick={() => navigate("/menu")}
              data-testid="orders-browse-btn"
            >
              Ver menú
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => (
              <Link
                key={order.order_id}
                to={`/order/${order.order_id}`}
                data-testid={`order-${order.order_id}`}
                className={`block bg-white rounded-2xl border border-gray-100 overflow-hidden animate-slide-up order-card ${order.status}`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-heading font-bold">
                        Pedido #{order.order_id.slice(-6)}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(order.created_at).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={`status-${order.status}`}
                    >
                      {STATUS_LABELS[order.status]}
                    </Badge>
                  </div>

                  <div className="text-sm text-muted-foreground mb-3">
                    {order.items.slice(0, 2).map((item, i) => (
                      <span key={i}>
                        {item.quantity}x {item.name}
                        {i < Math.min(order.items.length - 1, 1) && ", "}
                      </span>
                    ))}
                    {order.items.length > 2 && (
                      <span> +{order.items.length - 2} más</span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="font-bold text-primary">
                      {order.total.toFixed(2)} €
                    </p>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MobileLayout>
  );
}
